import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getUserItemPreferences,
  getMasterEquipment,
  getUserEquipment,
  getMasterEquipmentDefinitions,
  getExcludedItems,
} from '../services/api';
import {
  Box,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  useDisclosure,
  Divider,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useColorModeValue,
  Spinner,
  IconButton,
} from '@chakra-ui/react';
import { CheckCircleIcon, DownloadIcon } from '@chakra-ui/icons';
import SelectedEquipmentModal from './SelectedEquipmentModal';
import { saveAs } from 'file-saver';
import Encoding from 'encoding-japanese';
import { getImageUrl } from '../utils/imageUrl';
// import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import SignatureCanvas from 'react-signature-canvas';
import { useRef } from 'react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';


// 除外プロパティリストを関数外で定義
const unUseFields = [
  'id', 'user_id', 'created_at', 'updated_at', 'is_liked', 'is_fixed', 'locked_at',
  'signature_url', 'material_files', 'preferenceId', 'recommended', 'default_quantity',
  '_rid', '_self', '_etag', '_attachments', '_ts', 'type', 'tenant_id', 'images',
  'item_type', 'item_id', 'external_link_url', 'external_link_name',
  'rooms', 'floorNames', 'floorCount'
];

function SignaturePadModal({ isOpen, onClose, onSave }) {
  const sigPadRef = useRef();
  const [error, setError] = useState('');

  const handleClear = () => {
    sigPadRef.current.clear();
    setError('');
  };
  const handleSave = () => {
    if (sigPadRef.current.isEmpty()) {
      setError('サインを入力してください');
      return;
    }
    sigPadRef.current.getCanvas().toBlob(blob => {
      onSave(blob);
    });
  };
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>サインして一括確定</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={3}>
            <SignatureCanvas
              ref={sigPadRef}
              penColor="black"
              backgroundColor="#fff"
              canvasProps={{ width: 400, height: 200, style: { border: '1px solid #ccc', borderRadius: 8 } }}
            />
            {error && <Text color="black">{error}</Text>}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={handleClear} mr={3}>クリア</Button>
          <Button bg="black" color="white" onClick={handleSave}>サインして一括確定</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// 共通Excel出力関数
async function exportConfirmedExcel({
  equipmentDefinitions,
  summaryByType,
  unUseFields,
  isFunctionEnabled,
  totalByType,
  grandTotal,
  taxAmount,
  totalWithTax,
  includeSummary = false,
  signatureBuffer = null,
  saveAsFile = true,
        fileName = '概算見積一覧.xlsx',
} = {}) {
  const workbook = new ExcelJS.Workbook();
  // --- 概要シートを最初に追加 ---
  if (includeSummary) {
    const existing = workbook.getWorksheet('概要');
    if (existing) workbook.removeWorksheet(existing.id);
    const summarySheet = workbook.addWorksheet('概要', { position: 0 });
    let row = 1;
    summarySheet.getCell(`A${row}`).value = '概算見積概要';
    summarySheet.getCell(`A${row}`).font = { bold: true, size: 16 };
    row += 2;
    summarySheet.getCell(`A${row}`).value = `日付: ${new Date().toLocaleDateString()}`;
    row++;
    summarySheet.getCell(`A${row}`).value = `小計（税抜）`;
    summarySheet.getCell(`B${row}`).value = grandTotal;
    row++;
    summarySheet.getCell(`A${row}`).value = `消費税`;
    summarySheet.getCell(`B${row}`).value = taxAmount;
    row++;
    summarySheet.getCell(`A${row}`).value = `合計（税込）`;
    summarySheet.getCell(`B${row}`).value = totalWithTax;
    row += 2;
    summarySheet.getCell(`A${row}`).value = 'ご署名:';
    row++;
    if (signatureBuffer) {
      const signImageId = workbook.addImage({ buffer: signatureBuffer, extension: 'png' });
      summarySheet.addImage(signImageId, {
        tl: { col: 0, row: row - 1 },
        ext: { width: 300, height: 120 }
      });
      row += 6;
    }
    summarySheet.getCell(`A${row}`).value = '※ こちらの金額は概算価格です。実際の価格は詳細な打ち合わせの上で決定いたします。';
  }
  // --- 各itemtypeシート ---
  for (const key of Object.keys(equipmentDefinitions.equipment_types || {})) {
    const typeDef = equipmentDefinitions.equipment_types[key];
    const typeLabel = typeDef?.name || key;
    const items = summaryByType[key] || [];
    if (items.length === 0) continue;
    let allFields = Array.from(
      items.reduce((set, item) => {
        Object.keys(item).forEach(f => {
          if (!unUseFields.includes(f)) set.add(f);
        });
        return set;
      }, new Set())
    );
    // 特定のフィールドを除外してから、決まった順序で追加
    allFields = allFields.filter(f => f !== 'memo' && f !== 'quantity' && f !== 'features');
    // 特徴をメーカーの後に配置
    const nameIndex = allFields.indexOf('name');
    const manufacturerIndex = allFields.indexOf('manufacturer');
    if (manufacturerIndex !== -1) {
      allFields.splice(manufacturerIndex + 1, 0, 'features');
    } else if (nameIndex !== -1) {
      allFields.splice(nameIndex + 1, 0, 'features');
    } else {
      allFields.unshift('features');
    }
    allFields.push('memo');
    allFields.push('quantity');
    const fieldLabels = allFields.map(field => {
      if (field === 'memo') return 'メモ';
      if (field === 'quantity') return '数量';
      if (field === 'features') return '特徴';
      const fieldDef = typeDef.fields?.[field];
      return fieldDef?.label || field;
    });
    const labels = fieldLabels.concat(['小計', '状態']);
    const worksheet = workbook.addWorksheet(typeLabel);
    worksheet.addRow(labels);
    // データ行
    for (let rowIdx = 0; rowIdx < items.length; rowIdx++) {
      const item = items[rowIdx];
      const row = [];
      allFields.forEach(field => {
        row.push(item[field] != null ? item[field] : '');
      });
      // 小計
      row.push((item.price || 0) * (item.quantity || 1));
      // 状態
      if (isFunctionEnabled('lock') && item.locked_at) row.push('ロック済み');
      else if (item.is_fixed) row.push('暫定');
      else row.push('');
      worksheet.addRow(row);
    }
    worksheet.addRow([]);
    // 画像貼り付け（各アイテムごとに同じ列で縦並び、重ならないように行間を空ける）
    let imageStartRow = worksheet.lastRow.number + 1;
    const imageMeta = items.map(item => {
      if (item.images && Array.isArray(item.images) && item.images.length > 0) {
        return item.images.map(async (imageItem) => {
          const imageUrl = typeof imageItem === 'string' ? imageItem : imageItem.url;
          if (!imageUrl) return null;
          try {
            const res = await fetch(getImageUrl(imageUrl));
            const blob = await res.blob();
            const arrayBuffer = await blob.arrayBuffer();
            const buffer = new Uint8Array(arrayBuffer);
            const ext = imageUrl.split('.').pop().toLowerCase();
            const imgObjUrl = URL.createObjectURL(blob);
            const img = await new Promise((resolve, reject) => {
              const image = new window.Image();
              image.onload = () => resolve(image);
              image.onerror = reject;
              image.src = imgObjUrl;
            });
            let width = img.width;
            let height = img.height;
            const maxW = 80, maxH = 80;
            if (width > maxW || height > maxH) {
              const ratio = Math.min(maxW / width, maxH / height);
              width = Math.round(width * ratio);
              height = Math.round(height * ratio);
            }
            URL.revokeObjectURL(imgObjUrl);
            return { buffer, ext, width, height };
          } catch (e) {
            return null;
          }
        });
      }
      return [];
    });
    // 画像情報を全て解決
    const resolvedImageMeta = [];
    for (const metaArr of imageMeta) {
      if (metaArr && metaArr.length > 0) {
        resolvedImageMeta.push(await Promise.all(metaArr));
      } else {
        resolvedImageMeta.push([]);
      }
    }
    let maxImages = Math.max(...resolvedImageMeta.map(arr => arr.length));
    const rowHeights = [];
    for (let imgIdx = 0; imgIdx < maxImages; imgIdx++) {
      let maxH = 0;
      for (let itemIdx = 0; itemIdx < resolvedImageMeta.length; itemIdx++) {
        const meta = resolvedImageMeta[itemIdx][imgIdx];
        if (meta && meta.height > maxH) maxH = meta.height;
      }
      rowHeights.push(maxH > 0 ? maxH : 20);
    }
    for (let itemIdx = 0; itemIdx < items.length; itemIdx++) {
      let maxW = 0;
      for (let imgIdx = 0; imgIdx < resolvedImageMeta[itemIdx].length; imgIdx++) {
        const meta = resolvedImageMeta[itemIdx][imgIdx];
        if (meta && meta.width > maxW) maxW = meta.width;
      }
      if (maxW > 0) {
        worksheet.getColumn(itemIdx + 1).width = Math.ceil(maxW / 7) + 1;
      }
    }
    let currentRow = imageStartRow;
    for (let imgIdx = 0; imgIdx < maxImages; imgIdx++) {
      worksheet.getRow(currentRow).height = rowHeights[imgIdx];
      for (let itemIdx = 0; itemIdx < items.length; itemIdx++) {
        const meta = resolvedImageMeta[itemIdx][imgIdx];
        if (meta) {
          const imageId = workbook.addImage({
            buffer: meta.buffer,
            extension: meta.ext === 'png' ? 'png' : 'jpeg',
          });
          worksheet.addImage(imageId, {
            tl: { col: itemIdx, row: currentRow - 1 },
            ext: { width: meta.width, height: meta.height }
          });
        }
      }
      currentRow++;
    }
  }
  // ファイル保存
  const buf = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  if (saveAsFile) {
    saveAs(blob, fileName);
  }
  return blob;
}

export default function ConfirmedSummary() {
  const { targetUserId } = useParams();
  const { currentUser, isFunctionEnabled } = useAuth();
  const navigate = useNavigate();
  const [preferences, setPreferences] = useState([]);
  const [masterData, setMasterData] = useState({}); // { item_type: { item_id: masterItem } }
  const [userEquipmentData, setUserEquipmentData] = useState({}); // { item_type: { item_id: userEquipment } }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [equipmentDefinitions, setEquipmentDefinitions] = useState({});
  const [isLoadingDefinitions, setIsLoadingDefinitions] = useState(true);
  
  // 消費税率（10%）
  const TAX_RATE = 0.1;
  
  // モーダル関連のstate
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageAlt, setSelectedImageAlt] = useState('');

  const handleImageClick = (imageUrl, altText) => {
    setSelectedImage(imageUrl);
    setSelectedImageAlt(altText);
    onOpen();
  };

  // 設備詳細モーダル関連のstate
  const [selectedEquipmentItem, setSelectedEquipmentItem] = useState(null);
  const [isEquipmentModalOpen, setIsEquipmentModalOpen] = useState(false);

  const handleEquipmentRowClick = (item, equipmentType) => {
    setSelectedEquipmentItem({ ...item, item_type: equipmentType });
    setIsEquipmentModalOpen(true);
  };

  const closeEquipmentModal = () => {
    setIsEquipmentModalOpen(false);
    setSelectedEquipmentItem(null);
  };

  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false); // 仮: サーバ連携で判定予定
  const [excludedItems, setExcludedItems] = useState([]); // 見積もり外項目


  // 一括確定ボタン押下
  const handleBulkConfirm = () => {
    setIsSignatureModalOpen(true);
  };
  
  // 来場予約ボタン押下
  const handleVisitReservation = () => {
    // 既存の来場予約ページに遷移
    navigate('/visit-reservation');
  };
  
  // サイン取得後の処理
  const handleSignatureSave = async (signatureBlob) => {
    setIsSignatureModalOpen(false);
    const signatureArrayBuffer = await signatureBlob.arrayBuffer();
    const signatureBuffer = new Uint8Array(signatureArrayBuffer);
    // Excel生成
    const blob = await exportConfirmedExcel({
      equipmentDefinitions,
      summaryByType,
      unUseFields,
      isFunctionEnabled,
      totalByType,
      grandTotal,
      taxAmount,
      totalWithTax,
      includeSummary: true,
      signatureBuffer,
      saveAsFile: true,
      fileName: '概算見積一覧_確定.xlsx',
    });
    // クラウド保存API呼び出し
    try {
      await uploadConfirmedExcel(blob);
    } catch (e) {
      alert('クラウドへのExcel保存に失敗しました: ' + e.message);
    }
    setIsConfirmed(true);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (isLoadingDefinitions) return;
      setLoading(true);
      setError('');
      try {
        // 1. user_item_preferences取得
        const prefs = await getUserItemPreferences(targetUserId);
        setPreferences(prefs);

        // 2. 各設備種別のマスタデータを取得
        const masterMap = {};
        const userEquipmentMap = {};
        await Promise.all(
          Object.keys(equipmentDefinitions.equipment_types || {}).map(async key => {
            if (!equipmentDefinitions.equipment_types[key].enabled) return;
            try {
              // マスタデータの取得
              const masterList = await getMasterEquipment(key);
              const map = {};
              masterList.forEach(item => { map[item.id] = item; });
              masterMap[key] = map;

              // ユーザー登録設備の取得
              const userList = await getUserEquipment(key, targetUserId);
              const userMap = {};
              userList.forEach(item => { userMap[item.id] = item; });
              userEquipmentMap[key] = userMap;
            } catch (e) {
              masterMap[key] = {};
              userEquipmentMap[key] = {};
            }
          })
        );
        setMasterData(masterMap);
        setUserEquipmentData(userEquipmentMap);
        // 確定Excelの存在確認
        try {
          const exists = await existsConfirmedExcel();
          setIsConfirmed(!!exists);
        } catch (e) {
          setIsConfirmed(false);
        }
      } catch (err) {
        setError('データ取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [targetUserId, equipmentDefinitions]);

  useEffect(() => {
    const fetchDefinitions = async () => {
      const defs = await getMasterEquipmentDefinitions();
      setEquipmentDefinitions(defs);
      setIsLoadingDefinitions(false);
    };
    fetchDefinitions();
  }, []);

  // 見積もり外項目を取得
  useEffect(() => {
    const fetchExcludedItems = async () => {
      try {
        const items = await getExcludedItems();
        setExcludedItems(items || []); // nullやundefinedの場合は空配列
      } catch (error) {
        console.error('見積もり外項目の取得に失敗しました:', error);
        setExcludedItems([]); // エラー時は空配列を設定
      }
    };
    fetchExcludedItems();
  }, []);

  const equipmentList = useMemo(() => 
    Object.values(equipmentDefinitions.equipment_types || {}),
    [equipmentDefinitions]
  );

  // 設備種別ごとに確定アイテムをまとめる
  const summaryByType = useMemo(() => {
    const result = {};
    Object.keys(equipmentDefinitions.equipment_types || {}).map((key) => {
      result[key] = [];
    });
    preferences.forEach(pref => {
      if ((pref.is_fixed || pref.locked_at) && result[pref.item_type]) {
        // マスタ情報またはユーザー登録設備情報を紐付け
        const master = masterData[pref.item_type]?.[pref.item_id] || {};
        const userEquipment = userEquipmentData[pref.item_type]?.[pref.item_id] || {};
        if (master.id) {
          result[pref.item_type].push({ ...pref, ...master });
        }
        else if(userEquipment.id){
          result[pref.item_type].push({ ...pref, ...userEquipment });
        }
      }
    });
    return result;
  }, [preferences, masterData, userEquipmentData]);

  // 設備種別ごとの合計
  const totalByType = useMemo(() => {
    const result = {};
    for (const key in summaryByType) {
      result[key] = summaryByType[key].reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);
    }
    return result;
  }, [summaryByType]);

  // 全体合計（税抜き）
  const grandTotal = useMemo(() => {
    return Object.values(totalByType).reduce((sum, v) => sum + v, 0);
  }, [totalByType]);

  // 消費税額の計算
  const taxAmount = useMemo(() => {
    return Math.floor(grandTotal * TAX_RATE);
  }, [grandTotal]);

  // 税込み合計の計算
  const totalWithTax = useMemo(() => {
    return grandTotal + taxAmount;
  }, [grandTotal, taxAmount]);

  // 確定アイテムが全くないかチェック
  const hasNoConfirmedItems = useMemo(() => {
    return Object.values(summaryByType).every(items => items.length === 0);
  }, [summaryByType]);

  // CSVエクスポート用関数
  const handleExportCSV = () => {
    const headers = ['設備種別', '名称', 'メーカー', '特徴', '数量', '単価', 'メモ', '状態'];
    const rows = [];
    Object.keys(equipmentDefinitions.equipment_types || {}).forEach(key => {
      const typeLabel = equipmentDefinitions.equipment_types[key]?.name || key;
      (summaryByType[key] || []).forEach(item => {
        let state = '';
        if (isFunctionEnabled('lock') && item.locked_at) state = 'ロック済み';
        else if (item.is_fixed) state = '暫定';
        rows.push([
          typeLabel,
          item.name || '-',
          item.manufacturer || '-',
          item.features || '-',
          item.quantity || 1,
          item.price || '-',
          item.memo || '',
          state
        ]);
      });
    });
    let csv = '';
    csv += headers.map(val => `"${String(val).replace(/"/g, '""')}"`).join(',') + '\n';
    rows.forEach(row => {
      csv += row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(',') + '\n';
    });
    const shiftJISData = Encoding.convert(Encoding.stringToCode(csv), {
      to: 'SJIS',
      from: 'UNICODE'
    });
    const blob = new Blob([new Uint8Array(shiftJISData)], { type: 'text/csv;charset=shift-jis' });
    saveAs(blob, '一覧.csv');
  };

  // Excelエクスポート用関数
  const handleExportExcel = async () => {
    await exportConfirmedExcel({
      equipmentDefinitions,
      summaryByType,
      unUseFields,
      isFunctionEnabled,
      totalByType,
      grandTotal,
      taxAmount,
      totalWithTax,
      includeSummary: true, // 概要シートを必ず追加
      saveAsFile: true,
      fileName: '概算見積.xlsx',
    });
  };

  // 確定済みExcelダウンロード処理
  const handleDownloadConfirmedExcel = async () => {
    try {
      const blob = await downloadConfirmedExcel();
      saveAs(blob, '概算見積_確定.xlsx');
    } catch (e) {
      alert('確定Excelのダウンロードに失敗しました: ' + e.message);
    }
  };

  // 管理者による確定Excelクリア
  const handleClearConfirmedExcel = async () => {
    if (!window.confirm('本当に確定済みExcelをクリア（ロック解除）しますか？')) return;
    try {
      await clearConfirmedExcel(targetUserId);
      setIsConfirmed(false);
      alert('確定済みExcelをクリアしました');
    } catch (e) {
      alert('クリアに失敗しました: ' + e.message);
    }
  };

  if (loading || isLoadingDefinitions) return <Spinner size="xl" />;
  if (error) return <Text color="red.500">{error}</Text>;

  const isAdmin = targetUserId !== undefined && targetUserId !== currentUser?.uid;

  return (
    <Flex gap={8} p={4} w="100vw" h="100%" direction="column">
        <Heading size="md">概算見積</Heading>
       <Box 
            bg="gray.100"
            p={4}
            borderRadius="md"
            w="100%"
        >
        <Text>
          暫定・ロックしたアイテムを一覧で確認し、見積をご確認ください。ExcelやCSV出力をご活用ください。確定時には一括でロックし、確定済みExcelのダウンロードが可能です。
        </Text>
        <Text mt={2} fontSize="sm" color="gray.600">
          ※ 表中の行をクリックすると、詳細確認ができます
        </Text>
      </Box>

      {/* 確定アイテムがない場合の警告アラート */}
      {hasNoConfirmedItems && (
        <Box 
          bg="yellow.50" 
          p={6} 
          borderRadius="lg" 
          borderWidth="2px" 
          borderColor="yellow.300"
          mb={6}
          textAlign="center"
        >
          <VStack spacing={3}>
            <Text fontSize="2xl" fontWeight="bold" color="yellow.700">
              ⚠️ 確定アイテムがありません
            </Text>
            <Text fontSize="lg" color="yellow.600">
              概算見積もりを作成するには、設備選択ページでアイテムを暫定またはロックする必要があります。
            </Text>
            <Button
              as="a"
              href={targetUserId ? `/admin/user/${targetUserId}/equipment` : '/equipment'}
              colorScheme="yellow"
              size="lg"
              variant="outline"
              _hover={{ bg: "yellow.100" }}
            >
              設備選択ページに戻る
            </Button>
          </VStack>
        </Box>
      )}

      {/* 署名済みでない場合のみ出力・一覧・画像を表示 */}
      {!isConfirmed && (
        <>
          <Flex align="center" justify="space-between" mb={4}>
            <Flex gap={2}>
              <Button
                bg="black"
                color="white"
                _hover={{ bg: 'gray.700' }}
                onClick={handleExportCSV}
              >
                CSV出力
              </Button>
              <Button
                bg="black"
                color="white"
                _hover={{ bg: 'gray.700' }}
                onClick={handleExportExcel}
              >
                Excel出力
              </Button>
            </Flex>
          </Flex>
          <Divider mb={6} />
          {Object.keys(equipmentDefinitions.equipment_types || {}).map(key => (
            <Box key={key} mb={8}>
              <Heading size="md" color="gray.700" mb={2}>{equipmentDefinitions.equipment_types[key].label}</Heading>
              <VStack spacing={1} align="stretch" mb={2}>
                <Text fontSize="lg" color="black" fontWeight="bold">
                  {equipmentDefinitions.equipment_types[key].name} 小計（税抜）: ¥{totalByType[key]?.toLocaleString() || 0}
                </Text>
              </VStack>
              <Box overflowX="auto" mb={2}>
                <Table size="sm" variant="simple" minW="600px">
                  <Thead>
                    <Tr>
                      <Th>名称</Th>
                      <Th>メーカー</Th>
                      <Th>特徴</Th>
                      <Th>数量</Th>
                      <Th>単価</Th>
                      <Th>小計</Th>
                      <Th>メモ</Th>
                      <Th>状態</Th>
                    </Tr>
                  </Thead>
                <Tbody>
                  {!summaryByType || summaryByType[key].length === 0 ? (
                    <Tr>
                      <Td colSpan={8}>
                        <Box 
                          bg="yellow.50" 
                          p={4} 
                          borderRadius="md" 
                          borderWidth="2px" 
                          borderColor="yellow.300"
                          textAlign="center"
                        >
                          <VStack spacing={2}>
                            <Text fontSize="lg" fontWeight="bold" color="yellow.700">
                              ⚠️ 確定アイテムがありません
                            </Text>
                            <Text fontSize="sm" color="yellow.600">
                              この設備種別では、まだ暫定またはロックされたアイテムが選択されていません。
                            </Text>
                            <Text fontSize="xs" color="yellow.500">
                              設備選択ページでアイテムを選択してから、再度ご確認ください。
                            </Text>
                          </VStack>
                        </Box>
                      </Td>
                    </Tr>
                  ) : (
                    summaryByType[key].map(item => (
                      <Tr 
                        key={item.id} 
                        cursor="pointer" 
                        _hover={{ bg: "gray.50" }}
                        onClick={() => handleEquipmentRowClick(item, key)}
                      >
                        <Td>
                          {item.name || '-'}
                          {userEquipmentData[key]?.[item.item_id] && (
                            <Badge ml={2} colorScheme="black">ユーザー登録</Badge>
                          )}
                        </Td>
                        <Td>{item.manufacturer || '-'}</Td>
                        <Td>{item.features || '-'}</Td>
                        <Td>{item.quantity || 1}</Td>
                        <Td>¥{item.price?.toLocaleString() || '-'}</Td>
                        <Td>¥{((item.price || 0) * (item.quantity || 1)).toLocaleString()}</Td>
                        <Td>{item.memo || ''}</Td>
                        <Td>
                          {isFunctionEnabled('lock') && item.locked_at ? (
                            <Badge colorScheme="red">ロック済み</Badge>
                          ) : (
                            <Badge colorScheme="yellow">暫定</Badge>
                          )}
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
                </Table>
              </Box>
              {/* 設備画像の横スクロール表示 */}
              {summaryByType[key] && summaryByType[key].length > 0 && (
                <Box>
                  <HStack 
                    spacing={4} 
                    overflowX="auto" 
                    pb={2}
                    css={{
                      '&::-webkit-scrollbar': {
                        height: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: '#f1f1f1',
                        borderRadius: '10px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: '#888',
                        borderRadius: '10px',
                      },
                      '&::-webkit-scrollbar-thumb:hover': {
                        background: '#555',
                      },
                    }}
                  >
                    {summaryByType[key]
                      .filter(item => item.images && Array.isArray(item.images) && item.images.length > 0)
                      .map(item => {
                        // item.imagesが配列で存在する場合はその中のすべての画像を表示
                        return item.images.map((imageItem, imageIndex) => {
                          // 新旧形式対応：文字列かオブジェクトかを判定
                          const imageUrl = typeof imageItem === 'string' ? imageItem : imageItem.url;
                          const imageDescription = typeof imageItem === 'object' ? imageItem.description : '';
                          const fullImageUrl = getImageUrl(imageUrl);
                          return (
                            <Box key={`${item.id}-image-${imageIndex}`} flexShrink={0}>
                              <VStack spacing={2} align="center">
                                <Box
                                  borderWidth={1}
                                  borderColor="gray.200"
                                  borderRadius="md"
                                  overflow="hidden"
                                  boxShadow="sm"
                                  _hover={{ boxShadow: "md" }}
                                  transition="box-shadow 0.2s"
                                >
                                  {fullImageUrl ? (
                                    <Image
                                      src={fullImageUrl}
                                      alt={`${item.name || '設備画像'} ${imageIndex + 1}`}
                                      width="150px"
                                      height="120px"
                                      objectFit="contain"
                                      cursor="pointer"
                                      onClick={() => handleImageClick(fullImageUrl, `${item.name || '設備画像'} ${imageIndex + 1}`)}
                                      fallback={
                                        <Box 
                                          width="150px" 
                                          height="120px" 
                                          bg="gray.100" 
                                          display="flex" 
                                          alignItems="center" 
                                          justifyContent="center"
                                        >
                                          <Text fontSize="sm" color="gray.500">画像なし</Text>
                                        </Box>
                                      }
                                    />
                                  ) : (
                                    <Box 
                                      width="150px" 
                                      height="120px" 
                                      bg="gray.100" 
                                      display="flex" 
                                      alignItems="center" 
                                      justifyContent="center"
                                    >
                                      <Text fontSize="sm" color="gray.500">画像なし</Text>
                                    </Box>
                                  )}
                                </Box>
                                <Text fontSize="xs" color="gray.600" textAlign="center" maxW="150px" noOfLines={2}>
                                  {item.name || '名称なし'}
                                  {item.images.length > 1 && (
                                    <Text as="span" fontSize="xs" color="gray.400" ml={1}>
                                      ({imageIndex + 1}/{item.images.length})
                                    </Text>
                                  )}
                                </Text>
                              </VStack>
                            </Box>
                          );
                        });
                      })}
                  </HStack>
                </Box>
              )}
            </Box>
          ))}
        </>
      )}
      {/* 画像モーダル */}
      <Modal isOpen={isOpen} onClose={onClose} size="full" isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg="transparent" boxShadow="none" w="100vw" h="100vh" m={0}>
          <ModalCloseButton 
            color="white" 
            bg="blackAlpha.600" 
            _hover={{ bg: "blackAlpha.800" }}
            size={{ base: "md", md: "lg" }}
            zIndex={20}
            position="fixed"
            top={{ base: 2, md: 4 }}
            right={{ base: 2, md: 4 }}
          />
          <ModalBody p={0} display="flex" alignItems="center" justifyContent="center" w="100%" h="100%">
            {selectedImage && (
              <TransformWrapper
                initialScale={1}
                minScale={0.5}
                maxScale={4}
                centerOnInit={true}
                limitToBounds={false}
                smooth={true}
                wheel={{
                  step: 0.1,
                  wheelEnabled: true,
                  touchPadEnabled: true,
                }}
                pinch={{
                  step: 1,
                  disabled: false,
                }}
                doubleClick={{
                  step: 2,
                  mode: "toggle",
                }}
                onTransformed={(state) => {
                  // ズーム状態を追跡
                }}
              >
                {({ zoomIn, zoomOut, resetTransform, state }) => (
                  <TransformComponent
                    wrapperStyle={{
                      width: "100%",
                      height: "100vh",
                      maxWidth: "100%",
                      maxHeight: "100%",
                      touchAction: "none",
                    }}
                    contentStyle={{
                      width: "100%",
                      height: "100%",
                      touchAction: "none",
                    }}
                  >
                    <Box position="relative" w="100%" h="100%">
                      <Image
                        src={selectedImage}
                        alt={selectedImageAlt}
                        w="100%"
                        h="100vh"
                        objectFit="contain"
                        draggable={false}
                        userSelect="none"
                        style={{
                          touchAction: "none",
                          WebkitTouchCallout: "none",
                          WebkitUserSelect: "none",
                          KhtmlUserSelect: "none",
                          MozUserSelect: "none",
                          msUserSelect: "none",
                          userSelect: "none",
                        }}
                      />
                      {/* ズームインジケーター */}
                      {state && state.scale !== 1 && (
                        <Box
                          position="absolute"
                          top={4}
                          left="50%"
                          transform="translateX(-50%)"
                          bg="blackAlpha.600"
                          color="white"
                          px={3}
                          py={1}
                          borderRadius="md"
                          fontSize="sm"
                          zIndex={10}
                        >
                          {Math.round(state.scale * 100)}%
                        </Box>
                      )}
                      {/* ズームコントロール */}
                      <HStack
                        position="absolute"
                        bottom={4}
                        left="50%"
                        transform="translateX(-50%)"
                        spacing={2}
                        zIndex={10}
                      >
                        <IconButton
                          icon={<Text fontSize="sm">+</Text>}
                          onClick={() => zoomIn()}
                          bg="blackAlpha.600"
                          color="white"
                          _hover={{ bg: "blackAlpha.800" }}
                          size="sm"
                          aria-label="ズームイン"
                        />
                        <IconButton
                          icon={<Text fontSize="sm">-</Text>}
                          onClick={() => zoomOut()}
                          bg="blackAlpha.600"
                          color="white"
                          _hover={{ bg: "blackAlpha.800" }}
                          size="sm"
                          aria-label="ズームアウト"
                        />
                        <IconButton
                          icon={<Text fontSize="sm">⟲</Text>}
                          onClick={() => resetTransform()}
                          bg="blackAlpha.600"
                          color="white"
                          _hover={{ bg: "blackAlpha.800" }}
                          size="sm"
                          aria-label="リセット"
                        />
                      </HStack>
                    </Box>
                  </TransformComponent>
                )}
              </TransformWrapper>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
      
      {/* 署名済みでない場合のみ金額・注釈を表示 */}
      {!isConfirmed && (
        <>
          <Box bg="gray.50" p={4} borderRadius="md" mb={4}>
            <VStack spacing={2} align="stretch">
              <Text fontSize="md" color="gray.700" fontWeight="bold">
                小計（税抜）: ¥{grandTotal.toLocaleString()}
              </Text>
              <Text fontSize="md" color="gray.700" fontWeight="bold">
                消費税（10%）: ¥{taxAmount.toLocaleString()}
              </Text>
              <Divider borderColor="gray.300" />
              <Text fontSize="lg" color="black" fontWeight="bold">
                合計（税込）: ¥{totalWithTax.toLocaleString()}
              </Text>
            </VStack>
          </Box>
          
          {/* 見積もり外項目一覧 */}
          {excludedItems.length > 0 && (
            <Box bg="gray.50" p={3} borderRadius="md" mb={4} borderWidth={1} borderColor="gray.200">
              <Text fontSize="xs" color="gray.600" mb={2} fontWeight="medium">
                以下項目は見積もりに含まれていません。詳細は来店後ご案内致します。
              </Text>
              <Box overflowX="auto">
                <Table size="sm" variant="simple" minW="300px">
                  <Thead>
                    <Tr>
                      <Th fontSize="xs" color="gray.500" py={1} px={2}>項目名</Th>
                      <Th fontSize="xs" color="gray.500" py={1} px={2}>備考</Th>
                    </Tr>
                  </Thead>
                <Tbody>
                  {excludedItems.map((item) => (
                    <Tr key={item.id}>
                      <Td fontSize="xs" color="gray.600" py={1} px={2} fontWeight="medium">
                        {item.name}
                      </Td>
                      <Td fontSize="xs" color="gray.500" py={1} px={2}>
                        {item.remarks || '-'}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
                </Table>
              </Box>
            </Box>
          )}
          
          {/* 注釈 */}
          <Box p={4}>
            <Text fontSize="sm" color="gray.600" textAlign="left">
              ※ こちらの金額は概算価格です。実際の価格は詳細な打ち合わせの上で決定いたします。
            </Text>
          </Box>
        </>
      )}
      
      {/* 確定済みExcelダウンロードボタン（確定済み時のみ） */}
      {isConfirmed && (
        <Button colorScheme="blackAlpha" bg="black" color="white" onClick={handleDownloadConfirmedExcel} mb={2} alignSelf="flex-end">
          確定済みExcelダウンロード
        </Button>
      )}
      {/* 管理者用: 確定Excelクリアボタン（確定済み時のみ） */}
      {isConfirmed && isAdmin && (
        <Button colorScheme="red" bg="red.500" color="white" onClick={handleClearConfirmedExcel} mb={2} alignSelf="flex-end">
          確定済みExcelクリア（ロック解除）
        </Button>
      )}
      
      {/* 来場予約ボタン（一番下に配置） */}
      <Box mt={8} mb={4} display="flex" justifyContent="center">
        <Button
          onClick={handleVisitReservation}
          size="lg"
          height="80px"
          fontSize="xl"
          fontWeight="bold"
          bg="black"
          color="white"
          _active={{ bg: 'gray.900' }}
          px={12}
          borderRadius="lg"
          boxShadow="xl"
          borderWidth="3px"
          borderColor="gray.700"
          transition="all 0.3s ease"
          _hover={{
            transform: 'translateY(-3px) scale(1.02)',
            boxShadow: '2xl',
          }}
          animation="pulse 2s infinite"
          sx={{
            '@keyframes pulse': {
              '0%': {
                boxShadow: '0 0 0 0 rgba(0, 0, 0, 0.7)',
              },
              '70%': {
                boxShadow: '0 0 0 10px rgba(0, 0, 0, 0)',
              },
              '100%': {
                boxShadow: '0 0 0 0 rgba(0, 0, 0, 0)',
              },
            },
          }}
        >
          来店予約はこちら
        </Button>
      </Box>
      
      {/* 一括確定ボタン（未確定時のみ） */}
      {/* {!isConfirmed && (
        <>
          <Divider my={6} />
          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            maxW="400px"
          >
            <Button colorScheme="blackAlpha" bg="black" color="white" onClick={handleBulkConfirm} mb={2}>
              一括確定（サイン）
            </Button>
            <Text fontSize="sm" color="gray.600" textAlign="left">
              ※こちらは当社担当者と相談しながら実施ください。
            </Text>
          </Box>
        </>
      )} */}
    
      {/* サインモーダル */}
      <SignaturePadModal isOpen={isSignatureModalOpen} onClose={() => setIsSignatureModalOpen(false)} onSave={handleSignatureSave} />
      
      {/* 設備詳細モーダル */}
      {selectedEquipmentItem && (
        <SelectedEquipmentModal
          item={selectedEquipmentItem}
          config={equipmentDefinitions.equipment_types?.[selectedEquipmentItem.item_type]}
          onClose={closeEquipmentModal}
          onPreferenceChange={() => {}} // 概算見積もりページでは変更不可
          onUserEquipmentChange={() => {}} // 概算見積もりページでは変更不可
          isTsubo={false}
          equipmentDefinitions={equipmentDefinitions}
          fixedMap={{}}
          targetUserId={targetUserId}
          navigate={navigate}
          onEquipmentTypeChange={() => {}}
          isReadOnly={true} // 概算見積もりページでは読み取り専用
        />
      )}

    </Flex>
  );
} 